import React, { useRef, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import LoadingOverlay from '../components/LoadingOverlay';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmtFecha = (fechaStr) => {
    if (!fechaStr) return '';
    return new Date(fechaStr).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtHorario = (ini, fin) => {
    if (!ini && !fin) return 'Todo el día';
    if (ini && !fin) return `Desde ${ini}`;
    if (!ini && fin) return `Hasta ${fin}`;
    return `${ini} – ${fin}`;
};

const ESTADO_OPTIONS = [
    { label: 'Todas', value: '' },
    { label: 'Confirmada', value: 'Confirmada' },
    { label: 'Cancelada', value: 'Cancelada' },
];

// ── Componente ────────────────────────────────────────────────────────────────

const ReservasSUMAdmin = () => {
    const toast      = useRef(null);
    const HOST_API   = process.env.REACT_APP_URL;
    const { barrio } = useContext(BarrioContext);
    const { administracionId } = useAuth();

    const [reservas,         setReservas]         = useState([]);
    const [salones,          setSalones]          = useState([]);
    const [loading,          setLoading]          = useState(false);
    const [globalFilter,     setGlobalFilter]     = useState('');
    const [filtroEstado,     setFiltroEstado]     = useState('');
    const [filtroFecha,      setFiltroFecha]      = useState(null);
    const [filtroSalon,      setFiltroSalon]      = useState(null);
    const [vistaCalendario,  setVistaCalendario]  = useState(false);

    const addHour = (t) => {
        if (!t) return '01:00';
        const [h, m] = t.split(':').map(Number);
        return `${String(Math.min(h + 1, 23)).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    };

    const eventosCalendario = useMemo(() => reservas.map(r => {
        const fecha     = r.fecha?.substring(0, 10) ?? '';
        const todoElDia = !r.horaInicio && !r.horaFin;
        const color     = r.estado === 'Cancelada' ? '#f04438' : '#1570ef';
        return {
            id:              String(r.reservaId),
            title:           r.salonNombre,
            start:           todoElDia ? fecha : `${fecha}T${r.horaInicio || '00:00'}`,
            end:             todoElDia ? fecha : `${fecha}T${r.horaFin || addHour(r.horaInicio)}`,
            allDay:          todoElDia,
            backgroundColor: color,
            borderColor:     color,
            extendedProps:   { reserva: r },
        };
    }), [reservas]);

    // ── Fetch ──
    const fetchReservas = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${HOST_API}api/reservasSUM/GetReservas?administracionId=${administracionId}&barrioId=${barrio.id}`
            );
            setReservas(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las reservas', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, HOST_API, administracionId]);

    const fetchSalones = useCallback(async () => {
        if (!barrio?.id) return;
        try {
            const { data } = await axios.get(
                `${HOST_API}api/salones/GetSalones?administracionId=${administracionId}&barrioId=${barrio.id}`
            );
            setSalones([{ salonId: null, nombre: 'Todos' }, ...data]);
        } catch { /* silencioso */ }
    }, [barrio?.id, HOST_API, administracionId]);

    useEffect(() => { fetchReservas(); fetchSalones(); }, [fetchReservas, fetchSalones]);

    // ── Cancelar ──
    const handleCancelar = (r) => {
        confirmDialog({
            message: `¿Cancelar la reserva del ${fmtFecha(r.fecha)} en "${r.salonNombre}" de ${r.solicitante}?`,
            header: 'Cancelar reserva',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sí, cancelar',
            rejectLabel: 'No',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.put(`${HOST_API}api/reservasSUM/CancelReservaAdmin?reservaId=${r.reservaId}&administracionId=${administracionId}`);
                    fetchReservas();
                    toast.current.show({ severity: 'info', summary: 'Cancelada', detail: 'La reserva fue cancelada', life: 2500 });
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cancelar la reserva', life: 3000 });
                }
            },
        });
    };

    // ── Filtros aplicados localmente ──
    const reservasFiltradas = reservas.filter(r => {
        if (filtroEstado && r.estado !== filtroEstado) return false;
        if (filtroSalon  && r.salonId !== filtroSalon) return false;
        if (filtroFecha) {
            const df = filtroFecha.toISOString().substring(0, 10);
            if (new Date(r.fecha).toISOString().substring(0, 10) !== df) return false;
        }
        if (globalFilter) {
            const q = globalFilter.toLowerCase();
            return (
                r.solicitante?.toLowerCase().includes(q) ||
                r.salonNombre?.toLowerCase().includes(q)  ||
                r.motivo?.toLowerCase().includes(q)
            );
        }
        return true;
    });

    // ── Templates de columnas ──
    const estadoTemplate = (r) => {
        const map = {
            Confirmada: { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5' },
            Cancelada:  { bg: '#fef3f2', color: '#f04438', border: '#fecdca' },
        };
        const s = map[r.estado] || { bg: '#f2f4f7', color: '#667085', border: '#d0d5dd' };
        return (
            <span style={{
                background: s.bg, color: s.color, border: `1px solid ${s.border}`,
                fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
            }}>
                {r.estado}
            </span>
        );
    };

    const fechaTemplate     = (r) => fmtFecha(r.fecha);
    const horarioTemplate   = (r) => fmtHorario(r.horaInicio, r.horaFin);
    const motivoTemplate    = (r) => r.motivo
        ? <span style={{ color: '#667085', fontStyle: 'italic' }} title={r.motivo}>
            {r.motivo.length > 50 ? r.motivo.substring(0, 50) + '…' : r.motivo}
          </span>
        : <span style={{ color: '#d0d5dd' }}>—</span>;

    const accionesTemplate  = (r) => r.estado === 'Cancelada'
        ? null
        : (
            <Button
                icon="pi pi-ban"
                rounded text severity="danger"
                tooltip="Cancelar reserva"
                tooltipOptions={{ position: 'left' }}
                onClick={() => handleCancelar(r)}
            />
        );

    // ── Header con filtros ──
    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />

            <LoadingOverlay visible={loading} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.25rem' }}>
                <div>
                    <h4 style={{ margin: 0, color: '#101828' }}>Reservas</h4>
                    <p style={{ margin: '0.3rem 0 0', color: '#667085', fontSize: '0.88rem' }}>
                        Administración de reservas de espacios comunes
                    </p>
                </div>
                <div className="flex" style={{ border: '1px solid #d0d5dd', borderRadius: 8, overflow: 'hidden' }}>
                    <Button
                        icon="pi pi-list"
                        label="Lista"
                        onClick={() => setVistaCalendario(false)}
                        className={vistaCalendario ? 'p-button-text' : ''}
                        style={{ borderRadius: 0, border: 'none', padding: '0.5rem 0.9rem' }}
                    />
                    <Button
                        icon="pi pi-calendar"
                        label="Calendario"
                        onClick={() => setVistaCalendario(true)}
                        className={!vistaCalendario ? 'p-button-text' : ''}
                        style={{ borderRadius: 0, border: 'none', padding: '0.5rem 0.9rem' }}
                    />
                </div>
            </div>

            {!barrio?.id
                ? <p style={{ color: '#98a2b3' }}>Seleccioná un barrio para ver las reservas.</p>
                : vistaCalendario ? (
                    <div style={{ background: '#fff', borderRadius: 12, padding: '1rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                        <div style={{ fontSize: '0.78rem', color: '#667085', marginBottom: '0.75rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                            <span><span style={{ display:'inline-block', width:10, height:10, borderRadius:'50%', background:'#1570ef', marginRight:5 }}/>Confirmada</span>
                            <span><span style={{ display:'inline-block', width:10, height:10, borderRadius:'50%', background:'#f04438', marginRight:5 }}/>Cancelada</span>
                        </div>
                        <FullCalendar
                            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                            initialView="timeGridWeek"
                            locale={esLocale}
                            firstDay={1}
                            headerToolbar={{
                                left:   'prev,next today',
                                center: 'title',
                                right:  'dayGridMonth,timeGridWeek,timeGridDay',
                            }}
                            buttonText={{ today: 'Hoy', month: 'Mes', week: 'Semana', day: 'Día' }}
                            allDayText="Todo el día"
                            noEventsText="Sin reservas"
                            slotMinTime="06:00:00"
                            slotMaxTime="23:00:00"
                            slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
                            events={eventosCalendario}
                            height="auto"
                            eventContent={(info) => {
                                const r = info.event.extendedProps.reserva;
                                const isMonth = info.view.type === 'dayGridMonth';
                                return (
                                    <div style={{ padding: isMonth ? '1px 4px' : '3px 6px', fontSize: isMonth ? '0.71rem' : '0.78rem', overflow: 'hidden', lineHeight: 1.35, cursor: 'default' }}>
                                        <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {r.salonNombre}
                                        </div>
                                        {!isMonth && (
                                            <>
                                                <div style={{ opacity: 0.92 }}>{r.solicitante}</div>
                                                {r.motivo && (
                                                    <div style={{ opacity: 0.75, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {r.motivo}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                );
                            }}
                        />
                    </div>
                ) : (
                    <>
                        {/* Filtros fuera de la grilla */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'center' }}>
                                <Dropdown
                                    value={filtroSalon}
                                    options={salones}
                                    optionLabel="nombre"
                                    optionValue="salonId"
                                    onChange={(e) => setFiltroSalon(e.value)}
                                    placeholder="Espacio"
                                    className="p-inputtext-sm"
                                    style={{ minWidth: 160 }}
                                />
                                <Calendar
                                    value={filtroFecha}
                                    onChange={(e) => setFiltroFecha(e.value)}
                                    dateFormat="dd/mm/yy"
                                    placeholder="Filtrar por fecha"
                                    showIcon showButtonBar
                                    className="p-inputtext-sm"
                                    style={{ width: 190 }}
                                />
                                <Dropdown
                                    value={filtroEstado}
                                    options={ESTADO_OPTIONS}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(e) => setFiltroEstado(e.value)}
                                    className="p-inputtext-sm"
                                    style={{ minWidth: 140 }}
                                />
                                {(filtroSalon || filtroFecha || filtroEstado) && (
                                    <Button
                                        icon="pi pi-filter-slash"
                                        text
                                        severity="secondary"
                                        tooltip="Limpiar filtros"
                                        onClick={() => { setFiltroSalon(null); setFiltroFecha(null); setFiltroEstado(''); }}
                                    />
                                )}
                            </div>
                            <span className="p-input-icon-left">
                                <i className="pi pi-search" style={{ left: '0.75rem' }} />
                                <InputText
                                    value={globalFilter}
                                    onChange={(e) => setGlobalFilter(e.target.value)}
                                    placeholder="Buscar…"
                                    className="p-inputtext-sm"
                                    style={{ paddingLeft: '2rem', width: 200 }}
                                />
                            </span>
                        </div>

                        <DataTable
                            value={reservasFiltradas}
                            paginator
                            rows={15}
                            rowsPerPageOptions={[10, 15, 30, 50]}
                            emptyMessage="No hay reservas para mostrar"
                            sortField="fecha"
                            sortOrder={-1}
                            rowClassName={(r) => r.estado === 'Cancelada' ? 'opacity-50' : ''}
                            style={{ fontSize: '0.88rem' }}
                        >
                            <Column field="salonNombre"  header="Espacio"     sortable style={{ minWidth: 130 }} />
                            <Column field="solicitante"  header="Solicitante" sortable style={{ minWidth: 150 }} />
                            <Column field="fecha"        header="Fecha"       sortable body={fechaTemplate}    style={{ minWidth: 110 }} />
                            <Column header="Horario"     body={horarioTemplate} style={{ minWidth: 130 }} />
                            <Column header="Motivo"      body={motivoTemplate}  style={{ minWidth: 160 }} />
                            <Column field="estado"       header="Estado"      sortable body={estadoTemplate}   style={{ minWidth: 110 }} />
                            <Column header=""            body={accionesTemplate} style={{ width: 60, textAlign: 'center' }} />
                        </DataTable>
                    </>
                )
            }
        </>
    );
};

export default ReservasSUMAdmin;
